import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { setToast } from '@/entities/toast'
import { useGetApiSubscriptionTier } from '@/shared/apis/fastmile/generated/subscription-tier/subscription-tier'
import { usePostApiSubscriptionCreateCheckoutSession } from '@/shared/apis/fastmile/generated/subscription/subscription'
import { BillingIntervalEnum, CreateCheckoutSessionRequestDto, SubscriptionTierDto } from '@/shared/apis/fastmile/models/generated'
import { Icon, IconButton } from '@/shared/components'
import { useSubscription } from '@/shared/components/Subscription/SubscriptionContext'
import { SubscriptionTier } from '@/shared/components/Subscription/SubscriptionTier'
import { TextField } from '@/shared/components/TextField/TextField'
import { useAppDispatch } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { Box, Chip, CircularProgress, FormControlLabel, Grid2 as Grid, Radio, RadioGroup, styled, Typography } from '@mui/material'

import { getTierUIDetails } from './libs/getTierUIDetails'

export const NewSubscriptionPage: React.FC = () => {
  // Check if the upgrade page is redirected from the onboarding page
  const urlParams = new URLSearchParams(window.location.search)
  const fromOnboarding = urlParams.get('from') === 'onboarding'

  const { subscription, refetch, isLoading: isSubscriptionLoading } = useSubscription()
  const [vehicleCountInput, setVehicleCountInput] = useState('3')
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly')
  const [recommendedTier, setRecommendedTier] = useState<SubscriptionTierDto | null>(null)

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { data: subscriptionTiersResponse, isLoading: tiersLoading } = useGetApiSubscriptionTier()
  const createCheckoutSession = usePostApiSubscriptionCreateCheckoutSession()

  const tiers = useMemo(() => subscriptionTiersResponse?.data || [], [subscriptionTiersResponse])

  useEffect(() => {
    if (subscription?.vehicleQuantity) {
      setVehicleCountInput(subscription?.vehicleQuantity?.toString())
    }
  }, [subscription?.vehicleQuantity])

  // Handle checkout completion and subscription webhook delays
  useEffect(() => {
    const checkoutUrlParams = new URLSearchParams(window.location.search)
    const checkoutReturn = checkoutUrlParams.get('checkout_return')

    if (checkoutReturn === 'success') {
      // Clear URL parameters first
      const newUrl = window.location.pathname

      window.history.replaceState({}, '', newUrl)

      // Show success message
      dispatch(setToast({
        open: true,
        severity: 'success',
        title: 'Payment Successful!',
        content: 'Your subscription has been processed. We are updating your account...',
      }))

      refetch()
    } else if (checkoutReturn === 'cancel') {
      const newUrl = window.location.pathname

      window.history.replaceState({}, '', newUrl)

      dispatch(setToast({
        open: true,
        severity: 'info',
        title: 'Checkout Cancelled',
        content: 'Your subscription checkout was cancelled. No charges were made.',
      }))
    }
  }, [dispatch, refetch])

  // Setup settings page
  useEffect(() => {
    // This page is only shown for Free tier. If not free, we redirect to the manage subscription page.
    if (!isSubscriptionLoading && (!subscription || subscription.tierName !== 'Free')) {
      navigate('/subscription/manage')
    }
  }, [dispatch, navigate, subscription, isSubscriptionLoading])

  // Auto-select appropriate tier based on vehicle count
  useEffect(() => {
    const currentVehicleCount = parseInt(vehicleCountInput)

    if (tiers.length > 0 && !isNaN(currentVehicleCount) && currentVehicleCount > 0) {
      const sortedTiers = [...tiers].sort((a, b) => (a.vehicleLimit || 0) - (b.vehicleLimit || 0))

      let bestFitTier = sortedTiers.find(tier => (tier.vehicleLimit || 0) >= currentVehicleCount)

      if (!bestFitTier && sortedTiers.length > 0) {
        bestFitTier = sortedTiers[sortedTiers.length - 1]
      }

      if (bestFitTier && bestFitTier.id !== recommendedTier?.id) {
        setRecommendedTier(bestFitTier)
      } else if (!bestFitTier) {
        setRecommendedTier(null)
      }
    } else if (tiers.length > 0 && (isNaN(currentVehicleCount) || currentVehicleCount <= 0)) {
      const smallestTier = [...tiers].sort((a, b) => (a.vehicleLimit || 0) - (b.vehicleLimit || 0))[0]

      setRecommendedTier(smallestTier || null)
    }
  }, [vehicleCountInput, tiers, recommendedTier?.id])

  const handleVehicleCountInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    // Allow empty input for user experience, but parse as 0 or handle NaN for tier logic
    if (value === '' || /^\d+$/.test(value)) {
      setVehicleCountInput(value)
    }
  }

  const handleBillingIntervalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBillingInterval(event.target.value as 'monthly' | 'yearly')
  }

  const handleBackNavigation = () => {
    if (fromOnboarding) {
      navigate('/operations')
    } else {
      const from = location.state?.from?.pathname || '/operations'

      navigate(from)
    }
  }

  const handleTierSubscribe = async (tierToSubscribe: SubscriptionTierDto) => {
    if (!tierToSubscribe) return


    const currentVehicleCount = parseInt(vehicleCountInput)

    if (isNaN(currentVehicleCount) || currentVehicleCount <= 0) {
      dispatch(setToast({
        open: true,
        severity: 'warning',
        title: 'Invalid Vehicle Count',
        content: 'Please enter a valid number of vehicles.',
      }))

      return
    }

    if (!tierToSubscribe?.id) {
      dispatch(setToast({
        open: true,
        severity: 'error',
        title: 'Invalid tier selected',
        content: 'Please select a valid tier.',
      }))

      return
    }

    if (tierToSubscribe.name === 'Free') {
      if (fromOnboarding) {
        navigate('/operations?onboarding_complete=true')
      }

      return
    }

    if (tierToSubscribe.name === 'Enterprise') {
      dispatch(setToast({
        open: true,
        severity: 'info',
        title: 'Enterprise Plan',
        content: 'Redirecting to contact our sales team for Enterprise pricing.',
      }))

      return
    }

    // For paid tiers (Premium), create checkout session
    try {
      const checkoutRequest: CreateCheckoutSessionRequestDto = {
        vehicleQuantity: currentVehicleCount,
        billingInterval: billingInterval === 'monthly' ? BillingIntervalEnum.NUMBER_1 : BillingIntervalEnum.NUMBER_12,
        subscriptionTierId: tierToSubscribe.id,
        successUrl: fromOnboarding
          ? `${window.location.origin}/operations?onboarding_complete=true&upgrade_success=true`
          : `${window.location.origin}/upgrade?checkout_return=success`,
        cancelUrl: fromOnboarding
          ? `${window.location.origin}/operations?onboarding_complete=true&upgrade_cancelled=true`
          : `${window.location.origin}/upgrade?checkout_return=cancel`,
      }

      const response = await createCheckoutSession.mutateAsync({ data: checkoutRequest })

      if (response?.data?.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response?.data?.checkoutUrl
      } else {
        dispatch(setToast({
          open: true,
          severity: 'error',
          title: 'Checkout Error',
          content: 'Unable to create checkout session. Please try again.',
        }))
      }
    } catch (error) {
      console.error('Checkout session creation failed:', error)
      dispatch(setToast({
        open: true,
        severity: 'error',
        title: 'Error',
        content: 'Failed to create checkout session. Please try again.',
      }))
    }
  }

  const getSavingsPercentage = (tier: SubscriptionTierDto | null): string | null => {
    if (!tier || !tier.pricing || !tier.pricing.monthlyAmount || !tier.pricing.yearlyAmount || tier.pricing.monthlyAmount === 0 || tier.pricing.yearlyAmount === 0) {
      return null
    }

    const monthlyTotalForYear = tier.pricing.monthlyAmount * 12
    const yearlyCost = tier.pricing.yearlyAmount

    if (yearlyCost < monthlyTotalForYear) {
      const savings = Math.round(((monthlyTotalForYear - yearlyCost) / monthlyTotalForYear) * 100)

      return savings > 0 ? `Save ${savings}%` : null
    }

    return null
  }

  const representativePricedTier = tiers.find(t => t.pricing && t.pricing.monthlyAmount && t.pricing.monthlyAmount > 0)
  const annualSavingsGeneral = getSavingsPercentage(representativePricedTier || null)

  if (tiersLoading) {
    return (
      <StyledContainer>
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      </StyledContainer>
    )
  }

  return (
    <StyledContainer>
      <StyledBackButton onClick={handleBackNavigation} size={'large'} aria-label="Go back">
        <Icon icon={faArrowLeft} />
      </StyledBackButton>

      {(createCheckoutSession.isPending) && (
        <StyledLoadingContainer display="flex" justifyContent="center" alignItems="center" mt={4} gap={2}>
          <CircularProgress size={20} />
          <Typography>
            {createCheckoutSession.isPending
              ? 'Creating checkout session...'
              : 'Activating your subscription...'
            }
          </Typography>
        </StyledLoadingContainer>
      )}
      <StyledHeader>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          {fromOnboarding ? 'Choose your plan' : 'Pricing for every fleet size'}
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph sx={{ fontWeight: 400 }}>
          {fromOnboarding
            ? 'Complete your setup by selecting the plan that fits your needs'
            : 'Choose the plan that best fits your operation size and needs'
          }
        </Typography>
      </StyledHeader>

      <StyledInputSection>
        <Grid container spacing={6} alignItems="center" justifyContent="center">
          <Grid sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <Typography variant="body1" component="label" htmlFor="vehicle-count-input" sx={{ fontWeight: 500, mr: 1 }}>
              Fleet Size:
            </Typography>
            <TextField
              id="vehicle-count-input"
              round
              fullWidth
              type="text"
              value={vehicleCountInput}
              onChange={handleVehicleCountInputChange}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              sx={{ maxWidth: pxToRem(48), marginRight: pxToRem(14) }}
            />
            <Typography variant="body1" sx={{ ml: -1 }}>runs</Typography>
          </Grid>
          <Grid sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <Typography variant="body1" component="label" sx={{ fontWeight: 500, mr: 1 }}>
              Bill me:
            </Typography>
            <RadioGroup
              row
              aria-label="billing interval"
              name="billingInterval"
              value={billingInterval}
              onChange={handleBillingIntervalChange}
            >
              <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
              <FormControlLabel
                value="yearly"
                control={<Radio />}
                label={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    Annually
                    {annualSavingsGeneral && (
                      <Chip label={annualSavingsGeneral} color="info" size="small" sx={{ ml: 1, backgroundColor: 'info', color: 'info.darker' }} />
                    )}
                  </Box>
                }
              />
            </RadioGroup>
          </Grid>
        </Grid>
      </StyledInputSection>

      <StyledTiersGrid container spacing={3} justifyContent="center">
        {tiers?.map((tier) => {
          const uiDetails = getTierUIDetails(tier.name)

          if (!uiDetails) { // Skip rendering if no UI details are defined for this tier name
            console.warn(`No UI details found for tier: ${tier.name}`)

            return null
          }

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tier.id || tier.name}>
              <SubscriptionTier
                tier={tier}
                description={uiDetails.description}
                features={uiDetails.features}
                billingInterval={billingInterval}
                onSelect={() => handleTierSubscribe(tier)}
                isRecommended={recommendedTier?.id === tier.id}
                isDisabled={!fromOnboarding && tier.name === 'Free'}
                actionButtonText={
                  createCheckoutSession.isPending
                    ? 'Processing...'
                    : uiDetails.actionButtonText
                }
              />
            </Grid>
          )
        })}
      </StyledTiersGrid>

    </StyledContainer>
  )
}

const StyledBackButton = styled(IconButton)`
  position: absolute;
  top: ${props => props.theme.spacing(2)};
  left: ${props => props.theme.spacing(2)};
  z-index: 1;
  
  ${(props) => props.theme.breakpoints.up('md')} {
    top: ${props => props.theme.spacing(3)};
    left: ${props => props.theme.spacing(4)};
  }
`

const StyledLoadingContainer = styled(Box)`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${pxToRem(8)} ${pxToRem(12)};
  border: 1px solid ${props => props.theme.palette.divider};
  box-shadow: 0 0px 6px rgba(0, 0, 0, 0.1);
  background-color: ${props => props.theme.palette.background.paper};
  border-radius: ${pxToRem(60)};
`

const StyledContainer = styled(Box)`
  position: relative;
  padding: ${(props) => props.theme.spacing(3, 2)};
  background-color: ${props => props.theme.palette.background.paper};
  min-height: 100vh;
  ${(props) => props.theme.breakpoints.up('md')} {
    padding: ${(props) => props.theme.spacing(4, 5)};
  }
`

const StyledHeader = styled(Box)`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing(5)};
  margin-top: ${props => props.theme.spacing(4)};
`

const StyledInputSection = styled(Box)`
  padding: ${props => props.theme.spacing(2.5, 3)};
  margin-bottom: ${props => props.theme.spacing(4)};
  margin-left: auto;
  margin-right: auto;
  background-color: ${props => props.theme.palette.background.paper};
`

const StyledTiersGrid = styled(Grid)`
  margin-bottom: ${props => props.theme.spacing(4)};
  max-width: ${pxToRem(1024)};
  margin: auto;
`